import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { ArrowRightCircle } from 'lucide-react'

import { AutocompleteCustomerContainer } from '@/components/AutocompleteCustomerContainer'
import { FormCapsLabel } from '@/components/ui/form-caps-label'
import { FormFieldLabel } from '@/components/ui/form-field-label'
import type { SearchOption } from '@/components/ui/Search'
import { UnderlineInput } from '@/components/ui/underline-input'
import { UnderlineTextarea } from '@/components/ui/underline-textarea'

interface LinkedCustomerDetails {
  fullName?: string
  externalId?: string
  contactEmail?: string
  contactPhone?: string
  contactAddress?: string
  invoiceName?: string
  invoiceEmail?: string
  invoicePhone?: string
  invoiceAddress?: string
  invoiceVat?: string
}

interface LinkedCustomerProps {
  mode: 'read' | 'edit'
  customerName: string
  customerId?: string
  customerPublicId?: string
  details?: LinkedCustomerDetails

  selectedCustomer?: SearchOption | null
  isImmutable?: boolean
  saveEnabled?: boolean
  onSelectCustomer?: (option: SearchOption | null) => void
  onSaveCustomer?: () => void
  onResetCustomer?: () => void
}

const saveButtonClasses = (active: boolean) =>
  [
    'inline-flex h-8 items-center rounded-md px-3 text-[11px] font-medium',
    active
      ? 'bg-blue-600 text-white hover:bg-blue-700'
      : 'bg-slate-100 text-slate-500',
  ].join(' ')

const resetButtonClasses = (active: boolean) =>
  [
    'inline-flex h-8 items-center rounded-md px-3 text-[11px] font-medium',
    active
      ? 'bg-slate-100 text-slate-700 hover:bg-slate-200'
      : 'bg-slate-100 text-slate-400',
  ].join(' ')

export function LinkedCustomer(props: LinkedCustomerProps) {
  const {
    mode,
    customerName,
    customerId,
    customerPublicId,
    details,
    selectedCustomer,
    isImmutable = false,
    saveEnabled = false,
    onSelectCustomer,
    onSaveCustomer,
    onResetCustomer,
  } = props

  const [isExpanded, setIsExpanded] = useState(false)

  const resolved = {
    fullName: details?.fullName ?? customerName ?? '',
    externalId: details?.externalId ?? '',
    contactEmail: details?.contactEmail ?? '',
    contactPhone: details?.contactPhone ?? '',
    contactAddress: details?.contactAddress ?? '',
    invoiceName: details?.invoiceName ?? customerName ?? '',
    invoiceEmail: details?.invoiceEmail ?? '',
    invoicePhone: details?.invoicePhone ?? '',
    invoiceAddress: details?.invoiceAddress ?? '',
    invoiceVat: details?.invoiceVat ?? '',
  }

  return (
    <div className="rounded-md border border-slate-200 bg-white p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <FormCapsLabel>Linked Customer</FormCapsLabel>

          {mode === 'edit' ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <AutocompleteCustomerContainer
                    value={selectedCustomer ?? null}
                    disabled={isImmutable}
                    onChange={(option) => onSelectCustomer?.(option)}
                  />
                </div>
                {customerId ? (
                  <Link
                    to="/customers/$id"
                    params={{ id: customerPublicId ?? customerId }}
                    className="text-blue-700 hover:text-blue-800"
                    aria-label="Open linked customer"
                    title="Open linked customer"
                  >
                    <ArrowRightCircle className="h-4 w-4" />
                  </Link>
                ) : null}
              </div>
              <button
                type="button"
                className="text-xs text-blue-700 hover:underline"
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                {isExpanded ? 'Hide customer details' : 'Show customer details'}
              </button>
            </div>
          ) : (
            <div className="space-y-2 text-xs">
              <div className="space-y-1">
                <FormFieldLabel>Name</FormFieldLabel>
                <div className="flex items-center gap-2">
                  <p className="text-sm text-slate-900">{customerName || '—'}</p>
                  {customerId ? (
                    <Link
                      to="/customers/$id"
                      params={{ id: customerPublicId ?? customerId }}
                      className="text-blue-700 hover:text-blue-800"
                      aria-label="Open linked customer"
                      title="Open linked customer"
                    >
                      <ArrowRightCircle className="h-4 w-4" />
                    </Link>
                  ) : null}
                </div>
              </div>
              <button
                type="button"
                className="text-xs text-blue-700 hover:underline"
                onClick={() => setIsExpanded((prev) => !prev)}
              >
                {isExpanded ? 'Hide customer details' : 'Show customer details'}
              </button>
            </div>
          )}
        </div>

        {mode === 'edit' ? (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className={resetButtonClasses(saveEnabled && !isImmutable)}
              disabled={!saveEnabled || isImmutable}
              onClick={onResetCustomer}
            >
              Reset
            </button>
            <button
              type="button"
              className={saveButtonClasses(saveEnabled && !isImmutable)}
              disabled={!saveEnabled || isImmutable}
              onClick={onSaveCustomer}
            >
              Save
            </button>
          </div>
        ) : null}
      </div>

      {isExpanded ? (
        <div className="space-y-3 text-xs">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Name</FormFieldLabel>
            <div className="flex items-center gap-2">
              <p className="text-sm text-slate-700">{resolved.fullName || '—'}</p>
              {customerId ? (
                <Link
                  to="/customers/$id"
                  params={{ id: customerPublicId ?? customerId }}
                  className="text-blue-700 hover:text-blue-800"
                  aria-label="Open linked customer"
                  title="Open linked customer"
                >
                  <ArrowRightCircle className="h-4 w-4" />
                </Link>
              ) : null}
            </div>
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Omni ID</FormFieldLabel>
            <UnderlineInput readOnly disableAutoSelect value={resolved.externalId || '—'} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Email</FormFieldLabel>
            <UnderlineInput readOnly disableAutoSelect value={resolved.contactEmail || '—'} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Phone</FormFieldLabel>
            <UnderlineInput readOnly disableAutoSelect value={resolved.contactPhone || '—'} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Address</FormFieldLabel>
            <UnderlineTextarea readOnly value={resolved.contactAddress || '—'} rows={2} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Invoice to</FormFieldLabel>
            <UnderlineInput readOnly disableAutoSelect value={resolved.invoiceName || '—'} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Invoice email</FormFieldLabel>
            <UnderlineInput readOnly disableAutoSelect value={resolved.invoiceEmail || '—'} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Invoice phone</FormFieldLabel>
            <UnderlineInput readOnly disableAutoSelect value={resolved.invoicePhone || '—'} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">Invoice address</FormFieldLabel>
            <UnderlineTextarea readOnly value={resolved.invoiceAddress || '—'} rows={2} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
          <div className="flex flex-col gap-1 sm:flex-row sm:items-end sm:gap-3">
            <FormFieldLabel className="sm:min-w-[7rem]">VAT</FormFieldLabel>
            <UnderlineInput readOnly disableAutoSelect value={resolved.invoiceVat || '—'} className="bg-transparent rounded-none px-0 py-0 text-slate-700 shadow-none" />
          </div>
        </div>
      ) : null}
    </div>
  )
}
